import React, { useState } from 'react';
import { ArrowRight, Brain, Lightbulb, Users, LineChart, ChevronDown, Star } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const benefits = [
  {
    icon: Brain,
    title: 'Work on Cutting-Edge Projects',
    description: `At Agility.AI, you'll work on innovative projects at the forefront of technology – from building AI systems that solve real-world problems, to designing automation software that revolutionizes how businesses operate. We're tackling challenges in automation, predictive analytics, data engineering, and more.`,
  },
  {
    icon: LineChart,
    title: 'Grow and Learn Continuously',
    description: `We are deeply committed to your professional growth. Being a young and agile firm, we offer a fertile ground for learning new skills and accelerating your career trajectory. You'll work alongside seasoned mentors and industry experts who are eager to share knowledge and help you succeed.`,
  },
  {
    icon: Users,
    title: 'Agile, Collaborative Culture',
    description: `We pride ourselves on a company culture that is open, inclusive, and intensely collaborative. We operate in small, empowered teams where every voice is heard and great ideas can come from anyone. You'll find that we have minimal bureaucracy – our focus is on getting things done and delivering value.`,
  },
  {
    icon: Lightbulb,
    title: 'Impact and Purpose',
    description: `Because we take on big challenges for our clients, the solutions you build could be streamlining a healthcare provider's operations or helping a city use data to improve services for thousands of people. The impact of your code and ideas will be visible and meaningful.`,
  },
];

const perks = [
  'Flexible work arrangements (remote, hybrid, or in-office)',
  'Competitive compensation packages',
  'Comprehensive health insurance',
  'Professional development budget',
  'Regular team events and activities',
  'Mental health and wellness support',
  'Generous paid time off',
  'Modern tools and equipment',
];

const jobOpenings = [
  {
    id: 'senior-ai-ml-engineer',
    title: 'Senior AI/ML Engineer',
    department: 'Engineering',
    location: 'Mumbai, India / Remote',
    type: 'Full-time',
    description: 'Lead the development of cutting-edge AI and machine learning solutions for enterprise clients. Work with large-scale data processing, model training, and deployment in production environments.',
    requirements: [
      '5+ years of experience in AI/ML development',
      'Strong proficiency in Python, TensorFlow, PyTorch',
      'Experience with cloud platforms (AWS, Azure, GCP)',
      'Knowledge of MLOps and model deployment',
      'Strong problem-solving and communication skills'
    ],
    responsibilities: [
      'Design and implement ML models for client projects',
      'Collaborate with data engineers on data pipeline development',
      'Mentor junior team members',
      'Present technical solutions to clients'
    ]
  },
  {
    id: 'data-engineer',
    title: 'Data Engineer',
    department: 'Engineering',
    location: 'London, UK / Remote',
    type: 'Full-time',
    description: 'Build robust data infrastructure and pipelines that power our AI solutions. Work with modern data stack technologies to ensure reliable, scalable data processing.',
    requirements: [
      '3+ years of experience in data engineering',
      'Proficiency in SQL, Python, and data pipeline tools',
      'Experience with cloud data platforms',
      'Knowledge of data warehousing concepts',
      'Strong attention to detail and data quality'
    ],
    responsibilities: [
      'Design and maintain data pipelines',
      'Optimize data storage and retrieval systems',
      'Ensure data quality and governance',
      'Collaborate with AI/ML teams on data requirements'
    ]
  },
  {
    id: 'business-analyst',
    title: 'Business Analyst - AI Solutions',
    department: 'Consulting',
    location: 'New York, USA / Remote',
    type: 'Full-time',
    description: 'Bridge the gap between business requirements and technical solutions. Work directly with clients to understand their needs and translate them into actionable AI strategies.',
    requirements: [
      '3+ years of business analysis experience',
      'Understanding of AI/ML concepts and applications',
      'Strong analytical and problem-solving skills',
      'Excellent communication and presentation abilities',
      'Experience with project management methodologies'
    ],
    responsibilities: [
      'Conduct client requirement analysis',
      'Develop AI solution proposals',
      'Manage project timelines and deliverables',
      'Facilitate stakeholder communications'
    ]
  }
];

const employeeTestimonials = [
  {
    name: 'Priya Sharma',
    role: 'Senior Data Scientist',
    image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?auto=format&fit=crop&q=80',
    quote: 'Working at Agility.AI has been transformative for my career. The projects are challenging, the team is brilliant, and the impact we make for clients is truly meaningful. I\'ve grown more in two years here than in my previous five years combined.',
    rating: 5
  },
  {
    name: 'James Wilson',
    role: 'AI Solutions Architect',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80',
    quote: 'The collaborative culture here is unmatched. Everyone is genuinely invested in each other\'s success, and the leadership team provides incredible mentorship. Plus, working on cutting-edge AI projects keeps me excited to come to work every day.',
    rating: 5
  }
];

const applicationProcess = [
  {
    step: 1,
    title: 'Application Review',
    description: 'Submit your application through our careers portal. Our team reviews all applications within 3-5 business days.'
  },
  {
    step: 2,
    title: 'Initial Screening',
    description: 'Phone or video call with our HR team to discuss your background, interests, and the role requirements.'
  },
  {
    step: 3,
    title: 'Technical Assessment',
    description: 'Complete a technical challenge or case study relevant to the position you\'re applying for.'
  },
  {
    step: 4,
    title: 'Team Interviews',
    description: 'Meet with potential team members and hiring managers to discuss technical skills and cultural fit.'
  },
  {
    step: 5,
    title: 'Final Decision',
    description: 'We\'ll make our decision within 48 hours of your final interview and extend an offer if you\'re selected.'
  }
];

const Careers = () => {
  const [expandedJob, setExpandedJob] = useState<string | null>(null);

  const toggleJobDetails = (jobId: string) => {
    setExpandedJob(expandedJob === jobId ? null : jobId);
  };

  const scrollToJobs = () => {
    const jobsSection = document.getElementById('job-openings');
    if (jobsSection) {
      jobsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
    <SEOHead
      title="Careers at Agility | Join Our AI Team"
      description="Join Agility and work on cutting-edge AI, automation, and data engineering projects. Explore career opportunities."
      url="https://www.agilitytech.ai/careers"
    />
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80"
            alt="Careers Banner"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl fade-in-up">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Join Our Agile Team
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              At Agility.AI, we're not just offering you a job – we're inviting you to be part of a
              visionary team that is transforming industries through technology and innovation. If you
              are passionate about working on cutting-edge AI projects, eager to grow your skills, and
              ready to embrace an agile, forward-thinking culture, then you'll feel right at home with us.
            </p>
            <a
              href="mailto:hello@agilitytech.ai"
              className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium
                hover:bg-teal-400 transition-all duration-300 inline-flex items-center space-x-2
                shadow-lg hover:shadow-xl transform hover:-translate-y-1 btn-primary"
            >
              <span>Join Our Team</span>
              <ArrowRight className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Join Agility.AI?
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We understand that top talent has choices, so we strive to make Agility.AI a place 
              where you can thrive professionally and personally.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8 shadow-lg hover:shadow-xl 
                  transition-all duration-300 transform hover:-translate-y-1 border border-gray-800/50
                  hover:bg-gray-900/70 card-hover fade-in-up"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center">
                      <benefit.icon size={24} className="text-teal-400" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                    <p className="text-gray-300 leading-relaxed">{benefit.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Employee Testimonials */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center fade-in-up">
            Hear From Our Team
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {employeeTestimonials.map((testimonial, index) => (
              <div
                key={index}
                className="testimonial-card bg-gray-800/50 p-8 rounded-xl border border-gray-700/50
                  hover:bg-gray-800/70 transition-all duration-300 transform hover:-translate-y-1 fade-in-up"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic leading-relaxed">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-gray-400 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Openings - Temporarily Disabled */}
      <section id="job-openings" className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Current Openings
            </h2>
            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 p-12">
              <p className="text-xl text-gray-300 mb-6">
                We're currently not hiring for specific positions, but we're always interested in connecting with talented individuals.
              </p>
              <p className="text-gray-400">
                If you're passionate about AI, automation, and data engineering, feel free to reach out to us at <a href="mailto:hello@agilitytech.ai" className="text-teal-400 hover:text-teal-300 underline">hello@agilitytech.ai</a> with your resume and portfolio.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center fade-in-up">
            Our Application Process
          </h2>
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {applicationProcess.map((step, index) => (
                <div
                  key={index}
                  className="flex items-start space-x-6 fade-in-up"
                >
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">{step.step}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-300">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits & Perks */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 text-center">
              Benefits & Perks
            </h2>
            <p className="text-xl text-gray-300 mb-12 text-center">
              We offer a comprehensive benefits package designed to support your well-being and growth.
            </p>

            <div className="bg-gray-900/50 backdrop-blur-sm rounded-xl shadow-lg p-8
              border border-gray-800/50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {perks.map((perk, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <ArrowRight className="flex-shrink-0 h-6 w-6 text-teal-400 mt-1" />
                    <span className="text-gray-300">{perk}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto fade-in-up">
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Shape the Future?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              We are always on the lookout for talented software engineers, data scientists, AI/ML
              specialists, business analysts, and agile project leaders. We value initiative and often
              create roles for exceptional people.
            </p>
            <a
              href="mailto:hello@agilitytech.ai"
              className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4
                rounded-md text-lg font-medium hover:bg-teal-400 transition-all duration-300 group btn-primary"
            >
              <span>Get In Touch</span>
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Careers;